import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import * as Api from "@/api";
import urls from "@/constants/Urls";
import FormGroup from "@/design/forms/FormGroup";
import InputSelect from "@/design/forms/InputSelect";
import { showSnackbar } from "@/store/reducers/app";
import Valkyrie, {
  viArrowDownToLine,
  viArrowUpToCloud,
  viFile,
  viSpinnerThird,
  viXmark,
} from "@sippy-platform/valkyrie";

import { Alert, AlertTitle, Button, ButtonGroup, Grid, Input, LinearProgress, Stack, Typography } from "@mui/material";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import ImportWebWorker from "./ImportWebWorker?worker";

function ImportPhonebook({ phonebook, refreshPhonebookData, contactGroup }) {
  const dispatch = useDispatch();
  const [importTargetGroup, setImportTargetGroup] = useState(contactGroup?.id ?? phonebook.groups[0]?.id);

  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [inputKey, setInputKey] = useState(() => Date.now());
  const [done, setDone] = useState(false);
  const [socketState, setSocketState] = useState("ready");
  const [socketError, setSocketError] = useState(false);
  const [importErrors, setImportErrors] = useState({ name: "", lines: [] });
  const [token, setToken] = useState({ token: "", expires: "" });

  // Create and manage worker
  const workerRef = useRef();

  // Get token
  useEffect(() => {
    Promise.resolve(Api.getWebSocketAuth()).then((response) => {
      setToken(response.data.token);
    });
  }, []);

  const onFileChange = (event) => {
    setFile(event.target.files ? event.target.files[0] : null);
  };

  const resetImportSource = () => {
    setImporting(false);
    setFile(null);
    setInputKey(Date.now());
    setProgress(0);
    setImportErrors({ name: "", lines: [] });
  };

  function uploadPhonebook() {
    setImportErrors({ name: file?.name, lines: [] });
    setImporting(true);
    setSocketError(false);
    setSocketState("ready");

    const formData = new FormData();
    formData.append("file", file);

    Promise.resolve(Api.importPhonebook(phonebook.id, importTargetGroup, formData)).catch((error) => {
      switch (error.response.status) {
        case 413:
          dispatch(showSnackbar("Couldn't import the CSV file because it is too large."));
          break;
        default:
          dispatch(
            showSnackbar(
              error.response.status <= 499
                ? "Couldn't import the CSV file due to a problem with the file."
                : "Couldn't import the CSV file due to an unknown error.",
            ),
          );
          break;
      }

      resetImportSource();
    });
  }

  useEffect(() => {
    if (phonebook.id && token.token) {
      workerRef.current = new ImportWebWorker();

      workerRef.current.postMessage({
        url: urls.ws,
        id: phonebook.id,
        token: token.token,
      });

      workerRef.current.onmessage = (e) => {
        const workerMessage = JSON.parse(e.data);

        switch (workerMessage.event) {
          case "phx_error":
            setSocketError(true);
            break;
          case "import_progress":
            setSocketError(false);
            setProgress(workerMessage.payload.progress ?? 0);

            if (!workerMessage.payload.inserted) {
              setImportErrors((prev) => ({
                ...prev,
                lines: [...prev.lines, workerMessage.payload.line.toString()],
              }));
            }
            break;
          case "import_done":
            setSocketError(false);
            setProgress(workerMessage.payload.progress ?? 0);
            refreshPhonebookData?.();

            if (workerMessage.payload.insertedCount === 0) {
              setImportErrors({ name: "", lines: [] });
              dispatch(showSnackbar("Failed to import any contacts. Check your CSV file for any errors."));
            } else if (workerMessage.payload.errorCount >= 1) {
              dispatch(
                showSnackbar(
                  `Imported ${workerMessage.payload.insertedCount} contact(s), failed to import ${workerMessage.payload.errorCount} contact(s).`,
                ),
              );
            } else {
              dispatch(showSnackbar(`Imported ${workerMessage.payload.insertedCount} contact(s).`));
            }
            setDone(true);
            break;
        }
      };

      return () => {
        workerRef.current?.terminate();
      };
    }
  }, [dispatch, phonebook.id, refreshPhonebookData, token.token]);

  useEffect(() => {
    if (done) {
      setTimeout(() => {
        setProgress(0);
      }, 2000);

      setTimeout(() => {
        setImporting(false);
        setFile(null);
        setInputKey(Date.now());
        setDone(false);
      }, 100);
    }
  }, [done]);

  useEffect(() => {
    // If we receive an error that isn't followed up, warn users that we cannot show the progress
    let timeoutId;

    if (socketError) {
      timeoutId = setTimeout(() => {
        setSocketState("error");
        setProgress(0);
        setImporting(false);
        setFile(null);
        setInputKey(Date.now());
        setDone(false);
        setSocketError(false);
        clearTimeout(timeoutId);
      }, 1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [socketError, setImporting]);

  useEffect(() => {
    if (done) {
      setTimeout(() => {
        setProgress(0);
      }, 2000);

      setTimeout(() => {
        setImporting(false);
        setFile(null);
        setInputKey(Date.now());
        setDone(false);
      }, 100);
    }
  }, [done]);

  // Template download
  const [downloadTemplate, setDownloadTemplate] = useState(false);

  function getPhonebookTemplate() {
    setDownloadTemplate(true);

    Promise.resolve(Api.getPhonebookTemplate("csv"))
      .then((response) => {
        fileDownload(response.data, `PhonebookImportTemplate.csv`);
        setDownloadTemplate(false);
      })
      .finally(() => setDownloadTemplate(false));
  }

  return (
    <FormGroup
      title="Import"
      actions={
        <Button
          color="primary"
          onClick={getPhonebookTemplate}
          startIcon={<Valkyrie icon={downloadTemplate ? viSpinnerThird : viArrowDownToLine} spin={downloadTemplate} />}
        >
          Get template
        </Button>
      }
    >
      <Grid size={12}>
        <Stack
          sx={{
            gap: 1,
          }}
        >
          {phonebook.groups.length >= 1 || contactGroup?.id ? (
            <Stack
              direction="row"
              sx={{
                alignItems: "stretch",
                gap: 1,
                height: 40,
              }}
            >
              <Typography sx={{ alignSelf: "center" }}>Import</Typography>
              <ButtonGroup>
                <Button
                  component="label"
                  role={undefined}
                  disabled={importing}
                  tabIndex={-1}
                  startIcon={<Valkyrie icon={viFile} />}
                  variant="outlined"
                >
                  {file?.name ?? "Select a file"}
                  <Input
                    type="file"
                    onChange={onFileChange}
                    key={inputKey}
                    sx={{
                      clip: "rect(0 0 0 0)",
                      clipPath: "inset(50%)",
                      height: "1px",
                      overflow: "hidden",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      whiteSpace: "nowrap",
                      width: 1,
                    }}
                  />
                </Button>
                {file && (
                  <Button onClick={resetImportSource} disabled={!file || importing} variant="outlined">
                    <Valkyrie icon={viXmark} />
                  </Button>
                )}
              </ButtonGroup>
              {!contactGroup && (
                <>
                  <Typography sx={{ alignSelf: "center" }}>into</Typography>
                  <InputSelect
                    id="accessLevel"
                    label="Contact group"
                    onChange={(e, value) => setImportTargetGroup(value)}
                    value={importTargetGroup}
                    selectValue="value"
                    selects={phonebook.groups?.map((group) => ({ value: group.id, title: group.name }))}
                    getOptionLabel={(option) => option.title}
                    sx={{ maxWidth: 200 }}
                  />
                </>
              )}
              <Button
                variant="contained"
                onClick={uploadPhonebook}
                startIcon={<Valkyrie icon={importing ? viSpinnerThird : viArrowUpToCloud} spin={importing} />}
                disabled={!(file && importTargetGroup) || importing}
              >
                Import
              </Button>
            </Stack>
          ) : (
            <Typography>Create a group to import a CSV file.</Typography>
          )}
          {(importing || progress > 0) && socketState === "ready" && (
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: "center",
              }}
            >
              <LinearProgress variant="determinate" value={progress} sx={{ width: 1 }} />
              <Typography level="body-sm" sx={{ width: 36, textAlign: "end" }}>
                {progress ?? 0}%
              </Typography>
            </Stack>
          )}
          {socketState === "error" && (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <Typography>
                The import status cannot be shown. Your phonebook is still being imported, but we cannot show you the
                progress or any errors that may occure.
              </Typography>
            </Alert>
          )}
          {importErrors.lines.length >= 1 && !importing && (
            <Alert severity="error" sx={{ mt: 0.5 }}>
              <AlertTitle>Import errors in {importErrors.name}</AlertTitle>
              <Typography>
                Error on line{" "}
                {importErrors.lines.length > 1
                  ? importErrors.lines.slice(0, -1).join(", ") +
                    ", and " +
                    importErrors.lines[importErrors.lines.length - 1]
                  : importErrors.lines[0].toString()}
              </Typography>
            </Alert>
          )}
        </Stack>
      </Grid>
    </FormGroup>
  );
}

ImportPhonebook.propTypes = {
  phonebook: PropTypes.object,
  refreshPhonebookData: PropTypes.func,
  contactGroup: PropTypes.object,
};

export default ImportPhonebook;
